/* AI Chat Asistanı — Sağ kenardan açılır/kapanır panel.
 * Tüm sayfalarda kullanılabilir; sayfa değişince state korunur (Layout düzeyinde mount edilir).
 */
import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { MessageSquare, X, Send, Loader2, Sparkles, Bot, User } from 'lucide-react';

const STORAGE_KEY = 'gk_ai_chat_history';

export default function AIChatAssistant() {
    const [open, setOpen] = useState(false);
    const [history, setHistory] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-50)));
        } catch { /* ignore */ }
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [history]);

    const sendMessage = async () => {
        const msg = input.trim();
        if (!msg || sending) return;
        const newUserTurn = { role: 'user', content: msg };
        const newHistory = [...history, newUserTurn];
        setHistory(newHistory);
        setInput('');
        setSending(true);
        try {
            const res = await api.post('/ai/chat', {
                message: msg,
                history: history.slice(-10),
                page_context: window.location.pathname,
            });
            setHistory(h => [...h, { role: 'assistant', content: res.data?.reply || '(Boş yanıt)' }]);
        } catch (e) {
            const errMsg = e?.response?.data?.detail || 'AI cevap üretemedi';
            setHistory(h => [...h, { role: 'assistant', content: `⚠️ ${errMsg}` }]);
        } finally {
            setSending(false);
        }
    };

    const clearHistory = () => {
        if (window.confirm('Sohbet geçmişini temizle?')) {
            setHistory([]);
            try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        }
    };

    return (
        <>
            {/* Açma butonu - sağ alt sabit */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="ai-chat-open"
                    className="fixed bottom-20 right-6 z-50 bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-full w-14 h-14 shadow-xl flex items-center justify-center transition-all hover:scale-105"
                    title="AI Asistana sor"
                >
                    <Sparkles className="w-6 h-6" />
                </button>
            )}

            {/* Panel - sağdan açılan */}
            <div
                className={`fixed top-0 right-0 z-50 h-full w-full sm:w-96 bg-white shadow-2xl border-l border-slate-200 transition-transform duration-300 ease-out flex flex-col ${
                    open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
                }`}
                data-testid="ai-chat-panel"
            >
                {/* Header */}
                <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5" />
                        <div>
                            <div className="font-semibold text-sm">AI Asistan</div>
                            <div className="text-[10px] opacity-90">Grand Kaptan CRM</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {history.length > 0 && (
                            <button
                                onClick={clearHistory}
                                className="text-xs px-2 py-1 rounded hover:bg-white/20"
                                data-testid="ai-chat-clear"
                                title="Sohbeti temizle"
                            >
                                Temizle
                            </button>
                        )}
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="ai-chat-close"
                            className="p-1 rounded hover:bg-white/20"
                            title="Kapat"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Mesajlar */}
                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50" data-testid="ai-chat-messages">
                    {history.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                            <Sparkles className="w-8 h-8 mx-auto mb-2 text-emerald-300" />
                            <p className="font-medium">Merhaba 👋</p>
                            <p className="mt-1 text-xs">Program hakkında her şeyi sorabilirsin:</p>
                            <div className="mt-3 space-y-1 text-xs">
                                <button onClick={() => setInput('bugün en çok tüketilen 5 ürün')} className="block w-full px-3 py-1.5 bg-white border border-slate-200 rounded text-left hover:border-emerald-300 hover:bg-emerald-50">
                                    • bugün en çok tüketilen 5 ürün
                                </button>
                                <button onClick={() => setInput('Lobby barını analiz et')} className="block w-full px-3 py-1.5 bg-white border border-slate-200 rounded text-left hover:border-emerald-300 hover:bg-emerald-50">
                                    • Lobby barını analiz et
                                </button>
                                <button onClick={() => setInput('bu hafta hangi bar en pahalı?')} className="block w-full px-3 py-1.5 bg-white border border-slate-200 rounded text-left hover:border-emerald-300 hover:bg-emerald-50">
                                    • bu hafta hangi bar en pahalı?
                                </button>
                                <button onClick={() => setInput('dün kişi başı maliyet ne kadardı?')} className="block w-full px-3 py-1.5 bg-white border border-slate-200 rounded text-left hover:border-emerald-300 hover:bg-emerald-50">
                                    • dün kişi başı maliyet ne kadardı?
                                </button>
                            </div>
                        </div>
                    )}
                    {history.map((turn, i) => (
                        <div key={i} className={`flex gap-2 ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`ai-msg-${turn.role}-${i}`}>
                            {turn.role === 'assistant' && (
                                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                    <Bot className="w-4 h-4 text-emerald-700" />
                                </div>
                            )}
                            <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                                turn.role === 'user'
                                    ? 'bg-emerald-600 text-white rounded-br-sm'
                                    : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm'
                            }`}>
                                {turn.content}
                            </div>
                            {turn.role === 'user' && (
                                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                    <User className="w-4 h-4 text-slate-600" />
                                </div>
                            )}
                        </div>
                    ))}
                    {sending && (
                        <div className="flex gap-2 justify-start" data-testid="ai-typing">
                            <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <Bot className="w-4 h-4 text-emerald-700" />
                            </div>
                            <div className="px-3 py-2 rounded-2xl bg-white border border-slate-200 text-slate-500 text-sm flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                yazıyor…
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-3 border-t border-slate-200 bg-white">
                    <div className="flex items-center gap-2">
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                            placeholder="Soru yaz..."
                            disabled={sending}
                            className="flex-1"
                            data-testid="ai-chat-input"
                        />
                        <Button
                            onClick={sendMessage}
                            disabled={sending || !input.trim()}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="ai-chat-send"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
